/**
 * AI 상세 이미지 — HTML 템플릿 (순수 함수, import 없음).
 *
 * 렌더러(detailImageRenderer.js)와 파일을 나눈 이유: 여기엔 브라우저 API 가 없어서 Node 에서도 그대로 불러
 * 샘플 데이터로 HTML 을 뽑아 헤드리스 Chrome 으로 디자인을 확인할 수 있다 (scratchpad 하네스).
 *
 * 디자인 원칙 — "정보지"가 아니라 "여행 매거진":
 *   · 사진이 주인공. 글은 사진 위나 아래에 짧게. 풀블리드(900px 꽉 채움) 사진을 아끼지 않는다.
 *   · 섹션마다 영문 소제목(eyebrow, 자간 넓힘) + 큰 한글 제목. 번호는 크게(01, 02).
 *   · 색은 강조색 하나 + 테마 배경·글자색. 강조색은 히어로 사진에서 뽑는다(renderer.extractAccentColor).
 *   · 인라인 스타일만 쓴다 — html2canvas 결과가 페이지 CSS 에 영향받지 않게. html2canvas 1.4 는 object-fit 을 몰라
 *     사진은 전부 background-image cover 로 그리고, box-shadow·filter 도 못 그리므로 쓰지 않는다(미리보기와 저장본이 같아야 한다).
 */

/** 렌더 폭(CSS px). 쇼핑몰 상세 규격(860~1000)에 맞춘다. 실제 픽셀은 scale 배. */
export const DETAIL_WIDTH = 900;
/** 좌우 여백과 본문 폭 */
const PAD = 64;
const INNER = DETAIL_WIDTH - PAD * 2; // 772

export const DETAIL_THEMES = [
  { id: 'modern', label: '모던 (흰 배경)', hint: '흰 배경에 고딕 제목. 어떤 상품에나 무난합니다.' },
  { id: 'warm', label: '따뜻한 (크림 배경·세리프 제목)', hint: '크림색 배경에 명조 제목. 감성·휴양 상품에 어울립니다.' },
  { id: 'dark', label: '다크 (짙은 배경)', hint: '짙은 배경에 밝은 글자. 프리미엄·야경 상품에 어울립니다.' },
];

/** 강조색. 'auto' 는 렌더러가 히어로 사진에서 뽑은 색으로 바꿔 넘긴다(buildDetailHtml 에는 id 또는 #hex 가 온다). */
export const DETAIL_ACCENTS = [
  { id: 'auto', label: '사진에서 자동', color: null },
  { id: 'violet', label: '보라', color: '#6d28d9' },
  { id: 'blue', label: '파랑', color: '#1d4ed8' },
  { id: 'teal', label: '청록', color: '#0f766e' },
  { id: 'rose', label: '로즈', color: '#be123c' },
  { id: 'amber', label: '황토', color: '#b45309' },
];

export const SANS = "'Pretendard', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Malgun Gothic', sans-serif";
export const SERIF = "'Noto Serif KR', 'Nanum Myeongjo', Georgia, 'Batang', serif";

/** 테마 토큰. heading 은 제목 폰트, card 는 카드 배경, line 은 구분선, dark 는 다크 테마 여부(틴트 농도에 쓴다). */
const THEME_TOKENS = {
  modern: { bg: '#ffffff', text: '#111827', muted: '#5b6472', card: '#f4f5f7', line: '#e5e7eb', heading: SANS, dark: false },
  warm: { bg: '#fbf7f0', text: '#2b2420', muted: '#7a6f66', card: '#f2ebdf', line: '#e7dfd2', heading: SERIF, dark: false },
  dark: { bg: '#0f1115', text: '#f3f4f6', muted: '#a1a7b3', card: '#181c24', line: '#2a2f3a', heading: SANS, dark: true },
};
export const themeBackground = (theme) => (THEME_TOKENS[theme] || THEME_TOKENS.modern).bg;

/** 강조색의 연한 배경(#rrggbb + 투명도) */
const tint = (hex, alpha) => `${hex}${Math.round(alpha * 255).toString(16).padStart(2, '0')}`;

/** 사용자 글은 전부 이스케이프해서 넣는다 — 상품명·설명에 <, & 가 들어 있어도 템플릿이 깨지지 않게 */
export function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
/** 이스케이프 + 줄바꿈을 <br> 로 */
const escBr = (s) => esc(s).replace(/\r?\n/g, '<br>');
const pad2 = (n) => String(n).padStart(2, '0');
const list = (arr) => (Array.isArray(arr) ? arr.map((x) => String(x || '').trim()).filter(Boolean) : []);
const firstImg = (arr) => (Array.isArray(arr) ? arr.find(Boolean) : null) || null;

/**
 * 알약(pill) 라벨. 높이를 line-height 로 못 박고, 자간이 있으면 마지막 글자 뒤 자간만큼 오른쪽 padding 을 줄인다.
 *
 * data-badge: 렌더러(브라우저)가 이 속성을 보고 **캔버스로 그린 이미지로 바꿔치기** 한다(renderer.swapBadges).
 * 이유: html2canvas 는 글자 위치를 폰트 메트릭으로 따로 계산하는데, 굵은 굵기(800) 폰트 파일이 늦게 실리는 환경에서
 * 글자가 알약 안에서 위로 뜨는 문제가 실제로 있었다. 캔버스는 textBaseline='middle' 로 어떤 폰트든 정확히 가운데 찍는다.
 * 여기 HTML 알약은 Node 하네스와 이미지를 못 만들 때의 대체다.
 * @param {{ h:number, px:number, fs:number, ls?:number, weight?:number, bg:string, color:string, font?:string }} o
 */
function pill(text, o) {
  const ls = o.ls || 0;
  const padR = Math.max(0, o.px - Math.round(o.fs * ls));
  const spec = { text, h: o.h, px: o.px, fs: o.fs, ls, weight: o.weight || 800, bg: o.bg, color: o.color, font: o.font || SANS };
  return `<span data-badge="${esc(JSON.stringify(spec))}" style="display:inline-block;box-sizing:border-box;height:${o.h}px;line-height:${o.h}px;padding:0 ${padR}px 0 ${o.px}px;border-radius:999px;font-size:${o.fs}px;${ls ? `letter-spacing:${ls}em;` : ''}white-space:nowrap;vertical-align:middle;background:${o.bg};color:${o.color};font-family:${spec.font};font-weight:${spec.weight};">${esc(text)}</span>`;
}

/** 사진 칸: background-image cover. src 는 data URL 이라 따옴표가 필요 없다. */
const photoBox = (src, style) => `<div style="${style}background-image:url(${src});background-size:cover;background-position:center;background-repeat:no-repeat;"></div>`;

/** 섹션 머리: 영문 소제목(자간) + 큰 한글 제목 (+ 한 줄 설명). 아래 간격은 margin 이 아니라 padding — margin 은 섹션 밖으로 병합돼 조각 높이에서 빠진다 */
function sectionHead({ eyebrow, title, sub, accent, t, align = 'left', onDark = false }) {
  const color = onDark ? '#ffffff' : t.text;
  const mutedColor = onDark ? 'rgba(255,255,255,0.72)' : t.muted;
  return `
    <div style="text-align:${align};padding-bottom:36px;">
      <div style="font-family:${SANS};font-size:12px;font-weight:700;letter-spacing:0.22em;color:${onDark ? '#ffffff' : accent};margin-bottom:12px;">${esc(eyebrow)}</div>
      <h2 style="margin:0;font-family:${t.heading};font-size:34px;line-height:1.25;font-weight:800;color:${color};word-break:keep-all;">${esc(title)}</h2>
      ${sub ? `<p style="margin:12px 0 0;font-size:15px;line-height:1.6;color:${mutedColor};word-break:keep-all;">${escBr(sub)}</p>` : ''}
    </div>`;
}

// ── 1. 히어로 ────────────────────────────────────────────────────

function heroSection({ copy, image, accent, t, showStats }) {
  const H = 1120;
  const full = `position:absolute;left:0;top:0;width:${DETAIL_WIDTH}px;height:${H}px;`;
  const bgLayer = image
    ? photoBox(image, full)
    : `<div style="${full}background:linear-gradient(160deg,${accent} 0%,${tint(accent, 0.6)} 45%,#0b0f1a 100%);"></div>`;
  const badge = String(copy.badge || '').trim();
  const stats = showStats ? (Array.isArray(copy.stats) ? copy.stats : []).filter((s) => s && String(s.value || '').trim()).slice(0, 4) : [];
  const tiles = stats.length
    ? `<div style="display:flex;gap:12px;margin-top:40px;">${stats.map((s) => `
        <div style="flex:1;min-width:0;padding:16px 18px;border-radius:14px;background:rgba(255,255,255,0.13);border:1px solid rgba(255,255,255,0.28);">
          <div style="font-size:12px;font-weight:600;letter-spacing:0.06em;color:rgba(255,255,255,0.72);margin-bottom:6px;">${esc(s.label)}</div>
          <div style="font-size:17px;font-weight:700;color:#fff;line-height:1.3;word-break:keep-all;">${esc(s.value)}</div>
        </div>`).join('')}</div>`
    : '';
  return `
  <section style="position:relative;width:${DETAIL_WIDTH}px;height:${H}px;overflow:hidden;background:#0b0f1a;">
    ${bgLayer}
    <div style="${full}background:linear-gradient(180deg,rgba(0,0,0,0.35) 0%,rgba(0,0,0,0.05) 28%,rgba(0,0,0,0.10) 50%,rgba(0,0,0,0.82) 100%);"></div>
    <div style="position:absolute;left:${PAD}px;top:56px;right:${PAD}px;color:#fff;">
      <div style="display:flex;align-items:center;gap:14px;">
        <span style="display:inline-block;width:34px;height:2px;background:#fff;opacity:0.9;"></span>
        <span style="font-family:${SANS};font-size:13px;font-weight:700;letter-spacing:0.18em;text-shadow:0 1px 6px rgba(0,0,0,0.4);">${esc(badge || 'TRAVEL')}</span>
      </div>
    </div>
    <div style="position:absolute;left:${PAD}px;right:${PAD}px;bottom:56px;color:#fff;">
      <h1 style="margin:0;font-family:${t.heading};font-size:56px;line-height:1.18;font-weight:800;letter-spacing:-0.01em;text-shadow:0 2px 16px rgba(0,0,0,0.45);word-break:keep-all;">${escBr(copy.headline)}</h1>
      ${copy.subheadline ? `<p style="margin:18px 0 0;font-size:21px;line-height:1.5;font-weight:500;opacity:0.94;text-shadow:0 1px 10px rgba(0,0,0,0.45);word-break:keep-all;">${escBr(copy.subheadline)}</p>` : ''}
      ${tiles}
    </div>
  </section>`;
}

// ── 2. 소개 ──────────────────────────────────────────────────────

function introSection({ copy, accent, t }) {
  const intro = String(copy.intro || '').trim();
  if (!intro) return '';
  return `
  <section style="padding:80px ${PAD}px 72px;">
    <div style="font-family:${SANS};font-size:12px;font-weight:700;letter-spacing:0.22em;color:${accent};margin-bottom:22px;">ABOUT THIS JOURNEY</div>
    <p style="margin:0;font-family:${t.heading};font-size:23px;line-height:1.72;font-weight:${t.heading === SERIF ? 600 : 500};color:${t.text};word-break:keep-all;">${escBr(intro)}</p>
    <div style="width:56px;height:3px;background:${accent};margin-top:36px;"></div>
  </section>`;
}

// ── 3. 경로 지도 ─────────────────────────────────────────────────

function routeSection({ copy, map, accent, t }) {
  const route = (Array.isArray(copy.route) ? copy.route : []).filter((p) => p && String(p.name || '').trim());
  if (!map || route.length < 2) return '';
  const chips = route.map((p, i) => `
    ${i ? `<span style="color:${t.muted};font-size:14px;margin:0 2px;">→</span>` : ''}
    <span style="display:inline-block;box-sizing:border-box;height:36px;line-height:34px;padding:0 14px;border-radius:999px;background:${t.card};border:1px solid ${t.line};font-size:14px;font-weight:600;color:${t.text};white-space:nowrap;vertical-align:middle;">${esc(p.name)}${p.day ? `<span style="margin-left:6px;font-size:11px;font-weight:700;color:${accent};">D${p.day}</span>` : ''}</span>`).join('');
  return `
  <section style="padding:0 ${PAD}px 80px;">
    ${sectionHead({ eyebrow: 'ROUTE', title: '여행 경로', sub: `${route[0].name}에서 ${route[route.length - 1].name}까지, ${route.length}곳을 잇는 여정`, accent, t })}
    ${photoBox(map, `width:${INNER}px;height:480px;border-radius:22px;border:1px solid ${t.line};`)}
    <div style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin-top:22px;">${chips}</div>
  </section>`;
}

// ── 4. 포인트 ────────────────────────────────────────────────────

function highlightsSection({ copy, images, accent, t }) {
  const items = (Array.isArray(copy.highlights) ? copy.highlights : []).filter((h) => h && (String(h.title || '').trim() || String(h.desc || '').trim()));
  if (!items.length) return '';
  const rows = items.map((h, i) => {
    const img = firstImg(images[i]);
    const num = pad2(i + 1);
    const photo = img
      ? `<div style="position:relative;margin-bottom:26px;">
           ${photoBox(img, `width:${INNER}px;height:440px;border-radius:22px;`)}
           <div style="position:absolute;left:24px;top:24px;">${pill(`POINT ${num}`, { h: 32, px: 14, fs: 12, ls: 0.16, weight: 700, bg: 'rgba(10,12,18,0.62)', color: '#ffffff' })}</div>
         </div>`
      : '';
    return `
  <section style="padding:0 ${PAD}px ${i === items.length - 1 ? 88 : 64}px;">
    <div>
      ${photo}
      <div style="display:flex;gap:28px;align-items:flex-start;">
        <div style="flex:0 0 88px;font-family:${t.heading};font-size:60px;line-height:0.95;font-weight:800;color:${accent};letter-spacing:-0.02em;">${num}</div>
        <div style="flex:1;min-width:0;${img ? '' : `padding-left:24px;border-left:3px solid ${tint(accent, 0.5)};`}">
          <h3 style="margin:2px 0 12px;font-family:${t.heading};font-size:27px;line-height:1.35;font-weight:800;color:${t.text};word-break:keep-all;">${escBr(h.title)}</h3>
          <p style="margin:0;font-size:17px;line-height:1.75;color:${t.muted};word-break:keep-all;">${escBr(h.desc)}</p>
        </div>
      </div>
    </div>
  </section>`;
  }).join('');
  return `
  <section style="padding:0 ${PAD}px 0;">
    ${sectionHead({ eyebrow: 'HIGHLIGHTS', title: '이 여행의 포인트', accent, t })}
  </section>${rows}`;
}

// ── 5. 일정 ──────────────────────────────────────────────────────

/** 방문지 칩 + 식사·숙박 줄. dayMeta 가 없으면 빈 문자열. */
function dayMetaRows({ meta, accent, t }) {
  if (!meta) return '';
  const places = list(meta.places).slice(0, 8);
  const meals = String(meta.meals || '').trim();
  const hotels = list(meta.hotels);
  const chips = places.length
    ? `<div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:18px;">${places.map((p) => `<span style="display:inline-block;box-sizing:border-box;height:32px;line-height:30px;padding:0 12px;border-radius:999px;background:${t.bg};border:1px solid ${t.line};font-size:13px;font-weight:600;color:${t.text};white-space:nowrap;">${esc(p)}</span>`).join('')}</div>`
    : '';
  const info = [meals ? `<span><b style="color:${accent};font-weight:700;">식사</b>&nbsp; ${esc(meals)}</span>` : '', hotels.length ? `<span><b style="color:${accent};font-weight:700;">숙박</b>&nbsp; ${esc(hotels.join(', '))}</span>` : ''].filter(Boolean);
  const infoRow = info.length ? `<div style="display:flex;flex-wrap:wrap;gap:22px;margin-top:16px;font-size:13.5px;color:${t.muted};">${info.join('')}</div>` : '';
  return chips + infoRow;
}

function daysSection({ copy, images, accent, t, dayMeta }) {
  const days = Array.isArray(copy.days) ? copy.days : [];
  if (!days.length) return '';
  const cards = days.map((d, i) => {
    const img = firstImg(images[i]);
    const title = String(d.title || '').trim();
    const summary = String(d.summary || '').trim();
    const metaRows = dayMetaRows({ meta: dayMeta?.[d.day], accent, t });
    const dayPill = pill(`DAY ${pad2(d.day)}`, { h: 34, px: 14, fs: 13, ls: 0.12, weight: 800, bg: accent, color: '#ffffff' });
    const head = img
      ? `<div style="position:relative;width:${INNER}px;height:400px;">
           ${photoBox(img, `position:absolute;left:0;top:0;width:${INNER}px;height:400px;`)}
           <div style="position:absolute;left:0;top:0;width:${INNER}px;height:400px;background:linear-gradient(180deg,rgba(0,0,0,0.08) 0%,rgba(0,0,0,0.05) 45%,rgba(0,0,0,0.78) 100%);"></div>
           <div style="position:absolute;left:28px;top:24px;">${dayPill}</div>
           <div style="position:absolute;left:28px;right:28px;bottom:26px;color:#fff;">
             <h3 style="margin:0;font-family:${t.heading};font-size:30px;line-height:1.3;font-weight:800;text-shadow:0 2px 12px rgba(0,0,0,0.45);word-break:keep-all;">${escBr(title)}</h3>
           </div>
         </div>`
      : `<div style="display:flex;align-items:center;gap:16px;padding:26px 28px 0;">
           ${dayPill}
           <h3 style="margin:0;font-family:${t.heading};font-size:24px;line-height:1.3;font-weight:800;color:${t.text};word-break:keep-all;">${escBr(title)}</h3>
         </div>`;
    // 카드마다 별도 <section> — 렌더러가 최상위 섹션 단위로 잘라 그리므로(캔버스 크기 상한) 일정이 길어도 배율이 깎이지 않는다
    return `
  <section style="padding:0 ${PAD}px ${i === days.length - 1 ? 88 : 28}px;">
    <div style="border-radius:22px;overflow:hidden;background:${t.card};">
      ${head}
      <div style="padding:${img ? 26 : 18}px 28px 28px;">
        ${summary ? `<p style="margin:0;font-size:16px;line-height:1.75;color:${t.text};word-break:keep-all;">${escBr(summary)}</p>` : ''}
        ${metaRows}
      </div>
    </div>
  </section>`;
  }).join('');
  return `
  <section style="padding:0 ${PAD}px 0;">
    ${sectionHead({ eyebrow: 'ITINERARY', title: '일정 한눈에', sub: `${days.length}일 동안의 하루하루`, accent, t })}
  </section>${cards}`;
}

// ── 6. 포함 · 불포함 ─────────────────────────────────────────────

function includesSection({ copy, accent, t }) {
  const inc = list(copy.includes);
  const exc = list(copy.excludes);
  if (!inc.length && !exc.length) return '';
  const col = (title, items, mark, color, markBg) => `
    <div style="flex:1;min-width:0;">
      <h3 style="margin:0 0 18px;font-family:${SANS};font-size:13px;font-weight:700;letter-spacing:0.16em;color:${color};">${esc(title)}</h3>
      ${items.map((x) => `
        <div style="display:flex;gap:12px;align-items:flex-start;margin-bottom:12px;">
          <span style="flex:0 0 22px;width:22px;height:22px;border-radius:11px;background:${markBg};color:#fff;font-size:12px;font-weight:800;text-align:center;line-height:22px;margin-top:1px;">${mark}</span>
          <span style="font-size:15.5px;line-height:1.6;color:${t.text};word-break:keep-all;">${escBr(x)}</span>
        </div>`).join('')}
    </div>`;
  const cols = [inc.length ? col('INCLUDED · 포함', inc, '✓', accent, accent) : '', exc.length ? col('NOT INCLUDED · 불포함', exc, '✕', t.muted, t.dark ? '#3a4150' : '#b8bfca') : ''].filter(Boolean);
  return `
  <section style="padding:0 ${PAD}px 72px;">
    <div style="border-radius:22px;background:${t.card};padding:40px 40px 30px;display:flex;gap:40px;">${cols.join('')}</div>
  </section>`;
}

// ── 2b. 상품 정보 한눈에 (판매정보) ──────────────────────────────

/** 판매정보 구역의 사실을 라벨/값 표로. 항공·출발일·최소출발·인솔자·호텔등급·식사·쇼핑/옵션 등 */
function saleInfoSection({ copy, accent, t }) {
  const rows = (Array.isArray(copy.saleInfo) ? copy.saleInfo : []).filter((r) => r && String(r.label || '').trim() && String(r.value || '').trim()).slice(0, 10);
  if (!rows.length) return '';
  const cells = rows.map((r, i) => `
    <div style="box-sizing:border-box;width:50%;padding:18px ${i % 2 ? 0 : 28}px 18px ${i % 2 ? 28 : 0}px;border-top:1px solid ${t.line};">
      <div style="font-family:${SANS};font-size:12px;font-weight:700;letter-spacing:0.08em;color:${accent};margin-bottom:7px;">${esc(r.label)}</div>
      <div style="font-size:17px;line-height:1.5;font-weight:600;color:${t.text};word-break:keep-all;">${escBr(r.value)}</div>
    </div>`).join('');
  return `
  <section style="padding:0 ${PAD}px 80px;">
    ${sectionHead({ eyebrow: 'INFORMATION', title: '상품 정보 한눈에', accent, t })}
    <div style="display:flex;flex-wrap:wrap;border-bottom:1px solid ${t.line};">${cells}</div>
  </section>`;
}

// ── 4b. 특별 혜택 (특전) ─────────────────────────────────────────

function perksSection({ copy, accent, t }) {
  const perks = list(copy.perks).slice(0, 8);
  if (!perks.length) return '';
  const items = perks.map((x) => `
    <div style="box-sizing:border-box;width:50%;padding:8px 16px 8px 0;display:flex;gap:12px;align-items:flex-start;">
      <span style="flex:0 0 auto;width:26px;height:26px;border-radius:13px;background:${accent};color:#fff;font-size:13px;font-weight:800;text-align:center;line-height:26px;">✦</span>
      <span style="font-size:15.5px;line-height:1.65;color:${t.text};word-break:keep-all;padding-top:1px;">${escBr(x)}</span>
    </div>`).join('');
  return `
  <section style="padding:0 ${PAD}px 88px;">
    <div style="border-radius:22px;background:${tint(accent, t.dark ? 0.16 : 0.07)};padding:36px 40px 28px;">
      <div style="font-family:${SANS};font-size:12px;font-weight:700;letter-spacing:0.22em;color:${accent};margin-bottom:8px;">BENEFITS</div>
      <h3 style="margin:0 0 20px;font-family:${t.heading};font-size:26px;font-weight:800;color:${t.text};">이 상품만의 특별 혜택</h3>
      <div style="display:flex;flex-wrap:wrap;">${items}</div>
    </div>
  </section>`;
}

// ── 5b. 숙박 안내 (호텔) ─────────────────────────────────────────

function hotelsSection({ copy, accent, t }) {
  const hotels = (Array.isArray(copy.hotels) ? copy.hotels : []).filter((h) => h && String(h.name || '').trim()).slice(0, 8);
  if (!hotels.length) return '';
  const cards = hotels.map((h, i) => `
    <div style="box-sizing:border-box;width:50%;padding:0 ${i % 2 ? 0 : 12}px 24px ${i % 2 ? 12 : 0}px;">
      <div style="border-radius:18px;background:${t.card};padding:24px 26px;height:100%;box-sizing:border-box;">
        <div style="font-family:${SANS};font-size:11px;font-weight:700;letter-spacing:0.2em;color:${accent};margin-bottom:10px;">HOTEL</div>
        <div style="font-family:${t.heading};font-size:19px;line-height:1.35;font-weight:800;color:${t.text};word-break:keep-all;">${escBr(h.name)}</div>
        ${h.note ? `<div style="margin-top:8px;font-size:14px;line-height:1.6;color:${t.muted};word-break:keep-all;">${escBr(h.note)}</div>` : ''}
      </div>
    </div>`).join('');
  return `
  <section style="padding:0 ${PAD}px 64px;">
    ${sectionHead({ eyebrow: 'STAY', title: '숙박 안내', accent, t })}
    <div style="display:flex;flex-wrap:wrap;">${cards}</div>
  </section>`;
}

// ── 7. 이용 안내 (이용안내·예약안내·유의사항·취소/환불) ────────────

/** 구역별 그룹. notices 가 없고 옛 notes 만 있으면 "예약 전 확인하세요" 한 그룹으로 보여 준다. */
function noticesSection({ copy, accent, t }) {
  let groups = (Array.isArray(copy.notices) ? copy.notices : [])
    .map((g) => ({ title: String(g?.title || '').trim(), items: list(g?.items).slice(0, 10) }))
    .filter((g) => g.items.length)
    .slice(0, 6);
  if (!groups.length) {
    const notes = list(copy.notes);
    if (notes.length) groups = [{ title: '예약 전 확인하세요', items: notes }];
  }
  if (!groups.length) return '';
  const blocks = groups.map((g, gi) => `
    <div style="${gi ? `margin-top:28px;padding-top:28px;border-top:1px solid ${t.line};` : ''}">
      ${g.title ? `<h3 style="margin:0 0 14px;font-family:${t.heading};font-size:19px;font-weight:800;color:${t.text};padding-left:14px;border-left:4px solid ${accent};line-height:1.3;">${esc(g.title)}</h3>` : ''}
      ${g.items.map((x, i) => `
        <div style="display:flex;gap:14px;align-items:flex-start;margin-bottom:9px;">
          <span style="flex:0 0 auto;font-family:${SANS};font-size:12px;font-weight:800;color:${accent};margin-top:4px;">${pad2(i + 1)}</span>
          <span style="font-size:14.5px;line-height:1.65;color:${t.text};word-break:keep-all;">${escBr(x)}</span>
        </div>`).join('')}
    </div>`).join('');
  return `
  <section style="padding:0 ${PAD}px 88px;">
    ${sectionHead({ eyebrow: 'NOTICE', title: '이용 안내', sub: '예약 전에 꼭 확인해 주세요', accent, t })}
    <div style="border:1.5px solid ${tint(accent, 0.35)};border-radius:22px;padding:32px 36px 26px;background:${tint(accent, t.dark ? 0.1 : 0.04)};">${blocks}</div>
  </section>`;
}

// ── 8. 마무리 ────────────────────────────────────────────────────

function closingSection({ copy, image, contact, accent, t }) {
  const closing = String(copy.closing || '').trim();
  if (!closing && !contact) return '';
  const H = 520;
  const full = `position:absolute;left:0;top:0;width:${DETAIL_WIDTH}px;height:${H}px;`;
  const bg = image
    ? `${photoBox(image, full)}<div style="${full}background:rgba(8,10,16,0.62);"></div>`
    : `<div style="${full}background:linear-gradient(160deg,${accent} 0%,#0b0f1a 100%);"></div>`;
  return `
  <section style="position:relative;width:${DETAIL_WIDTH}px;height:${H}px;overflow:hidden;background:#0b0f1a;">
    ${bg}
    <div style="position:absolute;left:${PAD}px;right:${PAD}px;top:0;height:${H}px;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;color:#fff;">
      <span style="display:block;width:36px;height:2px;background:#fff;opacity:0.85;margin-bottom:30px;"></span>
      ${closing ? `<p style="margin:0;font-family:${t.heading};font-size:31px;line-height:1.5;font-weight:800;text-shadow:0 2px 14px rgba(0,0,0,0.45);word-break:keep-all;">${escBr(closing)}</p>` : ''}
      ${contact ? `<p style="margin:${closing ? 26 : 0}px 0 0;font-size:15px;letter-spacing:0.04em;opacity:0.85;">${esc(contact)}</p>` : ''}
    </div>
  </section>`;
}

// ── 조립 ─────────────────────────────────────────────────────────

/**
 * copy + 섹션별 이미지(data URL) → 인라인 스타일만 쓰는 HTML 문자열.
 * @param {object} p
 * @param {object} p.copy  requestDetailCopy 결과(사용자가 고친 것). route·stats·saleInfo·perks·hotels·notices 포함 (구역별 글이 없으면 빈 배열 → 그 섹션은 생략)
 * @param {{ hero?: string[], highlights?: string[][], days?: string[][], map?: string|null }} p.images  data URL (없는 곳은 빈 배열)
 * @param {string} p.accent  DETAIL_ACCENTS 의 id 또는 '#rrggbb'. 'auto' 는 렌더러가 미리 색으로 바꿔 넘긴다
 * @param {{ stats?, map?, saleInfo?, highlights?, perks?, days?, hotels?, includes?, notices?, closing? }} p.showSections  섹션 켜기/끄기 (기본 모두 켜짐)
 *   순서: 히어로 → 소개 → 상품 정보(판매정보) → 경로 지도 → 포인트 → 특별 혜택 → 일정 → 숙박 → 포함/불포함 → 이용 안내 → 마무리
 * @param {{ [day:number]: { places: string[], meals: string, hotels: string[] } }} p.dayMeta  일정 카드의 방문지 칩·식사·숙박
 * @returns {string} 루트 <div> 하나. 그 직계 자식 <section> 들이 렌더러의 조각(slice) 단위다 — 각각 따로 그려 이어 붙인다.
 */
export function buildDetailHtml({ copy, images = {}, theme = 'modern', accent = 'violet', contact = '', showSections = {}, dayMeta = {} }) {
  const t = THEME_TOKENS[theme] || THEME_TOKENS.modern;
  const accentColor = /^#[0-9a-f]{6}$/i.test(String(accent))
    ? String(accent).toLowerCase()
    : DETAIL_ACCENTS.find((a) => a.id === accent && a.color)?.color || DETAIL_ACCENTS[1].color;
  const show = { stats: true, map: true, saleInfo: true, highlights: true, perks: true, days: true, hotels: true, includes: true, notices: true, closing: true, ...showSections };
  const c = copy || {};
  const hiImgs = show.highlights ? images.highlights || [] : [];
  const dayImgs = show.days ? images.days || [] : [];
  // 마무리 배경: 일정·포인트 사진 중 마지막 것(이미 나온 사진이지만 어둡게 깔리는 배경이라 괜찮다)
  const closingImage = [...dayImgs.flat(), ...hiImgs.flat()].filter(Boolean).pop() || firstImg(images.hero);
  const parts = [
    heroSection({ copy: c, image: firstImg(images.hero), accent: accentColor, t, showStats: show.stats }),
    introSection({ copy: c, accent: accentColor, t }),
    show.saleInfo ? saleInfoSection({ copy: c, accent: accentColor, t }) : '',
    show.map ? routeSection({ copy: c, map: images.map || null, accent: accentColor, t }) : '',
    show.highlights ? highlightsSection({ copy: c, images: hiImgs, accent: accentColor, t }) : '',
    show.perks ? perksSection({ copy: c, accent: accentColor, t }) : '',
    show.days ? daysSection({ copy: c, images: dayImgs, accent: accentColor, t, dayMeta: dayMeta || {} }) : '',
    show.hotels ? hotelsSection({ copy: c, accent: accentColor, t }) : '',
    show.includes ? includesSection({ copy: c, accent: accentColor, t }) : '',
    show.notices ? noticesSection({ copy: c, accent: accentColor, t }) : '',
    show.closing ? closingSection({ copy: c, image: closingImage, contact: String(contact || '').trim(), accent: accentColor, t }) : '',
  ];
  return `<div style="width:${DETAIL_WIDTH}px;margin:0;padding:0;background:${t.bg};color:${t.text};font-family:${SANS};-webkit-font-smoothing:antialiased;box-sizing:border-box;">${parts.join('')}</div>`;
}
